/**
 * WelcomeScreen — Step 0
 *
 * Personalised welcome with 6-card step preview grid, CTA and skip link.
 */

import { Building2, Users, Settings, ShieldAlert, Bell, GitBranch, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTimesheet } from '@/contexts/TimesheetContext';
import { useOnboarding } from '@/contexts/OnboardingContext';

const PRIMARY = '#00796B';

const STEP_CARDS = [
  {
    icon: Building2,
    name: 'Company Setup',
    description: 'Add the companies your employees will log time against',
  },
  {
    icon: Users,
    name: 'User Access',
    description: 'Assign employees to the companies they can access',
  },
  {
    icon: Settings,
    name: 'Timesheet Settings',
    description: 'Configure submission deadlines and time entry rules',
  },
  {
    icon: ShieldAlert,
    name: 'Policy Rules',
    description: 'Set overage thresholds and policy enforcement severity',
  },
  {
    icon: Bell,
    name: 'Notifications',
    description: 'Set up reminders for submissions, approvals, and digests',
  },
  {
    icon: GitBranch,
    name: 'Approval Workflows',
    description: 'Define how timesheets are reviewed and approved',
  },
  {
    icon: Timer,
    name: 'Overtime Types',
    description: 'Create overtime categories for your companies',
  },
];

interface WelcomeScreenProps {
  onStart: () => void;
}

export function WelcomeScreen({ onStart }: WelcomeScreenProps) {
  const { currentEmployee } = useTimesheet();
  const { dismissOnboarding } = useOnboarding();

  // Extract first name from full name
  const firstName = currentEmployee?.name
    ? currentEmployee.name.split(' ')[0]
    : 'there';

  return (
    <div className="flex flex-col items-center justify-start w-full h-full overflow-y-auto py-8 px-6">
      <div className="w-full max-w-2xl mx-auto">
        {/* Logo + heading */}
        <div className="text-center mb-6">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl text-white text-2xl font-bold mb-4"
            style={{ backgroundColor: PRIMARY }}
          >
            T
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Time</h1>
        </div>

        {/* Personalised greeting */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Welcome, {firstName}! 👋
          </h2>
          <p className="text-sm text-gray-600 max-w-lg mx-auto leading-relaxed">
            Let's get your workspace set up. This wizard will guide you through seven
            configuration steps before your team starts submitting timesheets. It only takes
            around 10 minutes and you can always come back to it later.
          </p>
        </div>

        {/* Step preview cards — 2-column grid */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {STEP_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.name}
                className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg bg-white hover:border-teal-300 hover:bg-teal-50/30 transition-colors cursor-default"
              >
                <div
                  className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${PRIMARY}18` }}
                >
                  <Icon className="w-5 h-5" style={{ color: PRIMARY }} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 leading-tight">
                    {card.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-snug">
                    {card.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3">
          <Button
            size="lg"
            className="px-8 font-semibold"
            style={{ backgroundColor: PRIMARY, color: 'white' }}
            onClick={onStart}
          >
            Let's Start →
          </Button>
          <button
            className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            onClick={dismissOnboarding}
          >
            Skip onboarding
          </button>
        </div>
      </div>
    </div>
  );
}
